/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import React, { FunctionComponent } from 'react';

import {
    PRIMARY_BRANDING_LOGO_HEIGHT_1080P,
    PRIMARY_BRANDING_LOGO_WIDTH_1080P,
} from '../../../config';
import { ServicesClientImpl } from '../../../core/interactors/ServicesClientImpl';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import { TNavItem } from '../../../store/homescreen/homescreen.props';
import Clock from '../../Clock/Clock';
import { ClockTypes } from '../../Clock/ClockTypes';
import { Mousetrap } from '../../common';
import { ImageLoader } from '../../ImageLoader';
import { ImageLoaderTypes } from '../../ImageLoader/ImageLoaderTypes';

interface TopNavBarProps {
    navBarFocused: boolean;
    navBarIndex: number;
    navItems: TNavItem[];
    ghostNavBarIndex: number | null;
}

const TopNavBar: FunctionComponent<TopNavBarProps> = ({
    navBarFocused,
    navBarIndex,
    navItems,
    ghostNavBarIndex,
}): JSX.Element => {
    const services = new ServicesClientImpl();
    const brandingLogoUrl = services.getPartnerBrandingImageUrl(
        PRIMARY_BRANDING_LOGO_WIDTH_1080P,
        PRIMARY_BRANDING_LOGO_HEIGHT_1080P,
    );

    return (
        <div className="top-nav">
            <div className="top-nav-content">
                <div className="top-nav-logo">
                    <ImageLoader
                        src={brandingLogoUrl}
                        imageType={ImageLoaderTypes.SCREEN_HEADER_PRIMARY_BRANDING}
                    />
                </div>
                <div className="top-nav-items">
                    <div className="top-nav-home-section">
                        <div className="top-nav-home">
                            <span>{Locale.getString(LocaleKeys.HOME_SCREEN_TITLE)}</span>
                        </div>
                        <div className="top-nav-divider"></div>
                    </div>
                    <div className="top-nav-menu-section">
                        <div className="top-nav-menu-items">
                            {navItems.map((item, index) => {
                                const isFocused = navBarFocused && navBarIndex === index;
                                const isGhost = ghostNavBarIndex === index;
                                return (
                                    <Mousetrap key={item.key} value={`nav_${index}`}>
                                        <div
                                            className={`top-nav-item${isFocused ? ' top-nav-item--focused' : ''}${isGhost && !isFocused ? ' top-nav-item--ghost' : ''}`}
                                        >
                                            <span>{item.label}</span>
                                        </div>
                                    </Mousetrap>
                                );
                            })}
                            <Mousetrap value={`nav_${navItems.length}`}>
                                <div
                                    className={`top-nav-settings${
                                        navBarFocused && navBarIndex === navItems.length
                                            ? ' top-nav-settings--focused'
                                            : ''
                                    }${ghostNavBarIndex === navItems.length && !(navBarFocused && navBarIndex === navItems.length) ? ' top-nav-settings--ghost' : ''}`}
                                >
                                    {navItems.length > 0
                                        ? Locale.getString(
                                              LocaleKeys.SIDEBAR_NAVIGATION_MENU_SETTINGS_OPTION_TEXT,
                                          )
                                        : null}
                                </div>
                            </Mousetrap>
                        </div>
                    </div>
                </div>
            </div>
            <div className="top-nav-clock">
                <Clock type={ClockTypes.HOME_SCREEN_NAV_CLOCK} />
            </div>
        </div>
    );
};

export default TopNavBar;
